import { ErrorStateMatcher } from '@angular/material/core';
import {
  FloatLabelType,
  MatFormFieldAppearance,
  SubscriptSizing,
} from '@angular/material/form-field';
import {
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  Renderer2,
  SimpleChanges,
} from '@angular/core';
import {
  AsyncValidatorFn,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidatorFn,
} from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatSelectChange } from '@angular/material/select';

export type FormValidator = {
  validatorErrorName: string;
  validatorFn: ValidatorFn;
  errorMessage: string;
};
export type FormAsyncValidator = {
  validatorErrorName: string;
  validatorFn: AsyncValidatorFn;
  errorMessage: string;
};
export type FormLiftBehaviorValues = 'valueChanges' | 'statusChanges' | 'both';
export type SelectAddNewOption = {
  isEnabled: boolean;
  id: number;
  name: string;
};
export type SelectOption<T = any> = { value: T; viewValue: string };
export type SelectComponentOption<T = any> = {
  value: T;
  component: any;
  inputs: any;
};

export interface CustomInput {
  appearance?: MatFormFieldAppearance;
  asyncValidators?: FormAsyncValidator[];
  autocomplete?: string;
  classes?: string[];
  disabled?: boolean;
  kind: 'normal';
  label?: string;
  name: string;
  placeholder?: string;
  showErrors?: boolean;
  eventListener?: {
    eventHandler: Function;
    eventHandlerArgs: { [key: string]: any };
    eventName: string;
  };
  type: string;
  validators: FormValidator[];
  value?: any;
  readOnlyDisabled?: boolean;
  iconPrefix?: string;
  iconSuffix?: string;
  subscriptSizing?: SubscriptSizing;
  floatLabel?: FloatLabelType;
  hint?: string;
}
export interface CustomPhoneInput
  extends Omit<
    CustomInput,
    'eventListener' | 'kind' | 'readOnlyDisabled' | 'iconPrefix' | 'iconSuffix'
  > {
  countryCodeName: string;
  countryCodeNumber: string;
  kind: 'special';
  type: 'tel';
}
export interface CustomPhoneWithIconsInput
  extends Omit<
    CustomInput,
    | 'eventListener'
    | 'kind'
    | 'readOnlyDisabled'
    | 'iconPrefix'
    | 'iconSuffix'
    | 'validators'
    | 'asyncValidators'
  > {
  optionsMap: Map<string, string>;
  options: SelectOption[];
  kind: 'special';
  type: 'telWithIconSelect';
  isCountryCodeDisabled?: boolean;
  isPhoneNumberDisabled?: boolean;
  validators: { countryCode: FormValidator[]; phoneNumber: FormValidator[] };
  asyncValidators?: {
    countryCode: FormAsyncValidator[];
    phoneNumber: FormAsyncValidator[];
  };
}
export interface CustomSelectInput
  extends Omit<
    CustomInput,
    'autocomplete' | 'eventListener' | 'kind' | 'readOnlyDisabled'
  > {
  addNewOption?: SelectAddNewOption;
  emptyOption: string;
  kind: 'special';
  options: SelectOption[];
  type: 'select';
}
export interface CustomPasswordInput
  extends Omit<
    CustomInput,
    'eventListener' | 'kind' | 'readOnlyDisabled' | 'iconPrefix' | 'iconSuffix'
  > {
  kind: 'special';
  requirements?: { title: string; associatedErrorName: string }[];
  showRequirements: boolean;
  type: 'password';
}
export interface CustomNumericalInput
  extends Omit<CustomInput, 'kind' | 'autocomplete'> {
  _isLabelFloating?: boolean;
  allowOnlyDecimal?: boolean;
  currency?: 'inside' | 'outside' | null;
  defaultBehavior?: boolean;
  inputStyle?: { [key: string]: string };
  kind: 'special';
  percentage?: boolean;
  type: 'numerical';
}
export interface CustomDateInput
  extends Omit<
    CustomInput,
    'kind' | 'autocomplete' | 'iconPrefix' | 'iconSuffix'
  > {
  kind: 'special';
  type: 'date';
}
export interface CustomAutocompleteInput
  extends Omit<
    CustomInput,
    'autocomplete' | 'eventListener' | 'kind' | 'readOnlyDisabled'
  > {
  kind: 'special';
  options?: SelectOption[];
  componentOptions?: SelectComponentOption[];
  type: 'autocomplete';
  displayFn: (value: any) => string;
  viewComponent?: {
    component: any;
    inputs: any;
  };
}

export type CustomFormInputs =
  | CustomAutocompleteInput
  | CustomDateInput
  | CustomInput
  | CustomNumericalInput
  | CustomPasswordInput
  | CustomPhoneInput
  | CustomPhoneWithIconsInput
  | CustomSelectInput;

export interface CustomForm {
  name?: string;
  form: FormGroup;
}

export class CustomStateMatcher implements ErrorStateMatcher {
  constructor() {}
  isErrorState(control: FormControl | null): boolean {
    return !!(control?.errors && (control?.touched || control?.dirty));
  }
}

@Component({
  selector: 'app-form',
  templateUrl: './form.component.html',
  styleUrls: ['./form.component.scss'],
})
export class FormComponent implements OnInit {
  private _elRef = inject(ElementRef);
  private _formBuilder = inject(FormBuilder);
  private _renderer = inject(Renderer2);

  @Input() public containerClasses: string[] = [];
  @Input() public formLiftBehavior: FormLiftBehaviorValues = 'both';
  @Input() public formLiftOnInit: boolean = false;
  @Input() public formName: string = '';
  @Input() public inputs: Array<CustomFormInputs> = [];

  @Output() public formLift: EventEmitter<CustomForm> =
    new EventEmitter<CustomForm>();
  @Output() public addNewOptionEvent: EventEmitter<number> =
    new EventEmitter<number>();
  @Output() public autocompleteFiltersEvent: EventEmitter<{
    inputName: string;
    value: string;
  }> = new EventEmitter<{ inputName: string; value: string }>();
  @Output() public autocompleteOptionSelectedEvent: EventEmitter<{
    inputName: string;
    value: any;
  }> = new EventEmitter<{ inputName: string; value: string }>();
  @Output() public selectOptionChangeEvent: EventEmitter<{
    inputName: string;
    value: any;
  }> = new EventEmitter<{ inputName: string; value: string }>();

  private _destroy$: Subject<boolean> = new Subject<boolean>();
  protected form: FormGroup = {} as FormGroup;
  protected matcher: CustomStateMatcher = new CustomStateMatcher();
  protected minDate: Date = {} as Date;
  protected eventUnlisteners: Function[] = [];

  ngOnInit(): void {
    this.minDate = new Date();
    this.containerClasses =
      this.containerClasses.length > 0
        ? this.containerClasses
        : ['d-flex', 'flex-wrap'];
    this.formLiftBehavior = this.formLiftBehavior ?? 'both';
    this.form = this._formBuilder.group(this.createForm(this.inputs));
    this.applyLiftBehavior();
    if (this.formLiftOnInit) {
      this.formLift.emit({ name: this.formName, form: this.form });
    }
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['inputs'] && !changes['inputs'].firstChange) {
      let hasChanged = false;
      for (const [index, input] of changes['inputs'].currentValue.entries()) {
        if (
          input.disabled !== changes['inputs'].previousValue[index].disabled
        ) {
          hasChanged = true;
          break;
        } else if (input.kind === 'special' && input.type === 'tel') {
          if (
            input.countryCodeName !==
            changes['inputs'].previousValue[index].countryCodeName
          ) {
            hasChanged = true;
            break;
          }
        } else if (
          input.kind === 'special' &&
          input.type === 'telWithIconSelect'
        ) {
          if (
            input.isCountryCodeDisabled !==
              changes['inputs'].previousValue[index].isCountryCodeDisabled ||
            input.isPhoneNumberDisabled !==
              changes['inputs'].previousValue[index].isPhoneNumberDisabled
          ) {
            hasChanged = true;
            break;
          }
        }
      }
      if (hasChanged) {
        this.form = this._formBuilder.group(
          this.createForm(
            changes['inputs'].currentValue.map((input: any) => ({
              ...input,
              value: this.form.get(input.name)?.value,
            }))
          )
        );
        this.applyLiftBehavior();
        for (const controlName in this.form.value) {
          if (this.form.value[controlName]) {
            this.form.controls[controlName].markAsTouched();
          }
        }
      }
      //REFACTORING PENDING
      // this.form.get('name')?.enable();
    }
  }
  ngAfterViewInit() {
    this.eventUnlisteners = this.addEventsListeners(this.inputs);
  }
  ngOnDestroy() {
    this.eventUnlisteners.forEach((unlistener) => unlistener());
    this._destroy$.next(true);
    this._destroy$.unsubscribe();
  }
  protected addEventsListeners(formInputs: CustomFormInputs[]): Function[] {
    const eventUnlisteners: Function[] = [];
    for (const formInput of formInputs) {
      if (
        (formInput.kind === 'normal' || formInput.type === 'numerical') &&
        formInput.eventListener
      ) {
        const { eventHandlerArgs, eventHandler, eventName } =
          formInput.eventListener;
        const formInputElement = this._elRef.nativeElement.querySelector(
          `[data-input-name="${formInput.name}"]`
        );
        const unListener = this._renderer.listen(
          formInputElement,
          eventName,
          (rawEvent) => {
            eventHandler({
              ...eventHandlerArgs,
              form: this.form,
              event: rawEvent,
            });
          }
        );
        eventUnlisteners.push(unListener);
      }
    }
    return eventUnlisteners;
  }
  protected addNewOption(id: number): void {
    this.addNewOptionEvent.emit(id);
  }
  protected applyLiftBehavior(): void {
    if (this.formLiftBehavior === 'statusChanges') {
      this.form.statusChanges.pipe(takeUntil(this._destroy$)).subscribe(() => {
        this.formLift.emit({ name: this.formName, form: this.form });
      });
    } else if (this.formLiftBehavior === 'valueChanges') {
      this.form.valueChanges
        .pipe(takeUntil(this._destroy$))
        .subscribe((values) => {
          this.formLift.emit({ name: this.formName, form: this.form });
        });
    } else {
      this.form.valueChanges
        .pipe(takeUntil(this._destroy$))
        .subscribe((values) => {
          this.formLift.emit({ name: this.formName, form: this.form });
        });
      this.form.statusChanges.pipe(takeUntil(this._destroy$)).subscribe(() => {
        this.formLift.emit({ name: this.formName, form: this.form });
      });
    }
  }
  protected createForm(inputs: Array<CustomFormInputs>): any {
    return inputs.reduce((prev, current) => {
      if (current.kind === 'special' && current.type === 'telWithIconSelect') {
        const validatorsFn = {
          countryCode: current.validators.countryCode.map(
            (validator) => validator.validatorFn
          ),
          phoneNumber: current.validators.phoneNumber.map(
            (validator) => validator.validatorFn
          ),
        };
        const asyncValidatorsFn = {
          countryCode: current.asyncValidators?.countryCode?.map(
            (validator) => validator.validatorFn
          ),
          phoneNumber: current.asyncValidators?.phoneNumber?.map(
            (validator) => validator.validatorFn
          ),
        };
        return {
          ...prev,
          [current.name]: new FormGroup({
            countryCode: new FormControl(
              {
                value: current.value.countryCode ?? '',
                disabled: current.disabled
                  ? true
                  : current.isCountryCodeDisabled
                  ? true
                  : false,
              },
              {
                validators: [...validatorsFn.countryCode],
                asyncValidators: [...(asyncValidatorsFn.countryCode ?? [])],
              }
            ),
            phoneNumber: new FormControl(
              {
                value: current.value.phoneNumber ?? '',
                disabled: current.disabled
                  ? true
                  : current.isPhoneNumberDisabled
                  ? true
                  : false,
              },
              {
                validators: [...validatorsFn.phoneNumber],
                asyncValidators: [...(asyncValidatorsFn.phoneNumber ?? [])],
              }
            ),
          }),
        };
      } else {
        const validatorsFn = current.validators.map(
          (validator) => validator.validatorFn
        );
        const asyncValidatorsFn = current.asyncValidators?.map(
          (validator) => validator.validatorFn
        );
        return {
          ...prev,
          [current.name]: new FormControl(
            { value: current.value ?? '', disabled: current.disabled ?? false },
            [...validatorsFn],
            [...(asyncValidatorsFn ?? [])]
          ),
        };
      }
    }, {});
  }
  protected passwordTypeVisibilityModifier(input: HTMLInputElement): void {
    if (input.type === 'password') {
      this._renderer.setAttribute(input, 'type', 'text');
    } else {
      this._renderer.setAttribute(input, 'type', 'password');
    }
  }
  protected autocompleteFilters(input: HTMLInputElement) {
    this.autocompleteFiltersEvent.emit({
      inputName: input.dataset['inputName'] ?? '',
      value: input.value,
    });
  }
  protected autocompleteOptionsSelected(
    value: MatAutocompleteSelectedEvent,
    inputName: string
  ): void {
    this.autocompleteOptionSelectedEvent.emit({
      inputName,
      value: value.option.value,
    });
  }
  protected selectOptionsChange(
    value: MatSelectChange,
    inputName: string
  ): void {
    this.selectOptionChangeEvent.emit({
      inputName,
      value: value.value,
    });
  }
}
